import React, { useEffect, useRef, useState } from "react";
import { renderAsync } from "docx-preview";

interface DocxViewerModalProps {
  filename: string;
  fileUrl: string;
  onClose: () => void;
  subtitle?: string;
}

/**
 * Modal that renders a Microsoft Word (.docx) document in the browser using
 * docx-preview.
 *
 * Preserves all document fonts, margins, headings, bullet lists, tables,
 * colors, and inline styles with high fidelity on the client side.
 *
 * Downloads the raw .docx via window.fetch (which inherits the session token
 * from App.tsx's interceptor) and passes the resulting Blob to docx-preview.
 * Also generates a Blob URL for instant local download with the original filename.
 */
export function DocxViewerModal({
  filename,
  fileUrl,
  onClose,
  subtitle,
}: DocxViewerModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (isFullScreen) {
          setIsFullScreen(false);
        } else {
          onClose();
        }
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullScreen, onClose]);

  useEffect(() => {
    let objectUrl: string | null = null;
    let isCancelled = false;

    setLoading(true);
    setLoadError(null);

    fetch(fileUrl)
      .then(async (res) => {
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(body?.error ?? `Failed to load document (HTTP ${res.status})`);
        }
        return res.blob();
      })
      .then(async (blob) => {
        if (isCancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);

        if (containerRef.current) {
          // Clear any previous rendered children
          containerRef.current.innerHTML = "";
          await renderAsync(blob, containerRef.current, undefined, {
            inWrapper: true,
            ignoreWidth: false,
            ignoreHeight: false,
            className: "docx-rendered-body",
          });
        }
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (!isCancelled) {
          setLoadError(err instanceof Error ? err.message : "Failed to render document");
          setLoading(false);
        }
      });

    return () => {
      isCancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileUrl]);

  function handleBackdropClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div
      className={`modal-backdrop pdf-viewer-backdrop ${isFullScreen ? "viewer-fullscreen-backdrop" : ""}`}
      onClick={handleBackdropClick}
    >
      <div
        className={`modal-card pdf-viewer-card docx-viewer-card ${isFullScreen ? "viewer-fullscreen-card" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="pdf-viewer-header">
          <div className="pdf-viewer-title">
            <span className="ks-filename" title={filename}>
              📄 {filename}
            </span>
            {subtitle && <span className="mini muted pdf-viewer-scope">{subtitle}</span>}
          </div>

          <div className="pdf-viewer-actions">
            {/* Fullscreen toggle button */}
            <button
              type="button"
              className="btn mini"
              onClick={() => setIsFullScreen((prev) => !prev)}
              title={isFullScreen ? "Exit Fullscreen" : "Fullscreen"}
            >
              {isFullScreen ? "⤡ Exit Fullscreen" : "⛶ Fullscreen"}
            </button>
            {blobUrl && (
              <a
                className="btn mini"
                href={blobUrl}
                download={filename}
                title="Download .docx to your computer"
              >
                ↓ Download
              </a>
            )}
            <button className="btn mini" onClick={onClose} title="Close viewer">
              ✕ Close
            </button>
          </div>
        </div>

        {/* ── Document Body ── */}
        <div className="pdf-viewer-frame-wrap docx-viewer-wrap">
          {loadError ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "var(--red)",
                padding: 24,
                textAlign: "center",
              }}
            >
              {loadError}
            </div>
          ) : (
            <>
              {loading && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    color: "var(--muted)",
                  }}
                >
                  Loading and rendering Word document…
                </div>
              )}
              <div
                ref={containerRef}
                className="docx-viewer-content"
                style={{ display: loading ? "none" : "block" }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
